import type { RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

import inventorySetupRoutes from '../modules/inventory/routes'
import servicesSetupRoutes from '../modules/services/routes'
import procurementRoutes from '../modules/procurement/routes'
import expenseRoutes from '../modules/expense/routes'
import commissionRoutes from '../modules/commission/routes'
import importRoutes from '../modules/import/routes'
import businessRoutes from '../modules/business/routes'
import stockTakeRoutes from '../modules/stock-take/routes'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'login' },
  },
  {
    name: 'home',
    path: '/home',
    component: AppLayout,
    // No page of its own. Do not redirect here — Vue Router applies record redirects
    // before beforeEach, so a landing lookup would run on an empty store. The auth
    // guard sends `/home` to authorizedLandingRouteName() after hydration.
    meta: { requiresAuth: true },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'clients',
        path: 'clients',
        component: () => import('../pages/clients/ClientsPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'sales-list',
        path: 'sales',
        component: () => import('../pages/Sales/SalesList.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'new-sale',
        path: 'sales/new',
        component: () => import('../pages/Sales/NewSale.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'sale-details',
        path: 'sales/:saleNumber',
        component: () => import('../pages/Sales/SalesDetails.vue'),
        meta: { requiresAuth: true },
        props: true,
      },
      {
        name: 'payments',
        path: 'payments',
        component: () => import('../pages/finance/MakePayment.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'setup',
        path: '/setup',
        component: RouteViewComponent,
        children: [
          ...inventorySetupRoutes,
          ...servicesSetupRoutes,
          {
            name: 'roles',
            path: 'roles',
            component: () => import('../pages/setup/roles/RolesPage.vue'),
            meta: { requiresAuth: true },
          },
        ],
      },
      {
        name: 'inventory',
        path: '/inventory',
        component: RouteViewComponent,
        redirect: { name: 'inventory-dashboard' },
        children: [
          {
            name: 'inventory-dashboard',
            path: 'dashboard',
            component: () => import('../modules/inventory/pages/InventoryDashboardPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'inventory-stock-in',
            path: 'stock-in',
            component: () => import('../modules/inventory/pages/StockInPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'inventory-stock-out',
            path: 'stock-out',
            component: () => import('../modules/inventory/pages/StockOutPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            path: 'stock-adjustments',
            redirect: { name: 'inventory-stock-out' },
          },
          {
            name: 'inventory-transfers',
            path: 'transfers',
            component: () => import('../modules/inventory/pages/TransfersPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'inventory-transfer-create',
            path: 'transfers/create',
            component: () => import('../modules/inventory/pages/TransferCreatePage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'inventory-transfer-receive',
            path: 'transfers/:uuid/receive',
            component: () => import('../modules/inventory/pages/TransferReceivePage.vue'),
            meta: { requiresAuth: true },
            props: true,
          },
          {
            name: 'inventory-transfer-detail',
            path: 'transfers/:uuid',
            component: () => import('../modules/inventory/pages/TransferDetailPage.vue'),
            meta: { requiresAuth: true },
            props: true,
          },
          {
            name: 'inventory-items',
            path: 'items',
            component: () => import('../modules/inventory/pages/SetupItemsPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'inventory-locations',
            path: 'locations',
            component: () => import('../modules/inventory/pages/SetupLocationsPage.vue'),
            meta: { requiresAuth: true },
          },
          ...stockTakeRoutes,
        ],
      },
      {
        name: 'procurement',
        path: '/procurement',
        component: RouteViewComponent,
        children: [...procurementRoutes],
      },
      {
        name: 'finance',
        path: '/finance',
        component: RouteViewComponent,
        children: [
          {
            name: 'finance-index',
            path: '',
            component: () => import('../pages/finance/FinanceIndex.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'invoice-search',
            path: 'invoice-search',
            component: () => import('../pages/finance/InvoiceSearch.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'make-payment',
            path: 'make-payment',
            component: () => import('../pages/finance/MakePayment.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'invoice-payment',
            path: 'invoice-payment/:invoiceUuid?',
            component: () => import('../pages/finance/InvoicePayment.vue'),
            meta: { requiresAuth: true },
            props: true,
          },
          {
            name: 'payment-details',
            path: 'payment/:paymentNumber',
            component: () => import('../pages/finance/PaymentDetails.vue'),
            meta: { requiresAuth: true },
            props: true,
          },
          {
            name: 'payment-history',
            path: 'payment-history',
            component: () => import('../pages/finance/PaymentHistory.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'financial-reports',
            path: 'financial-reports',
            component: () => import('../pages/finance/FinancialReports.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'items-reports',
            path: 'items-reports',
            component: () => import('../modules/reports/pages/ItemsReportsPage.vue'),
            meta: { requiresAuth: true },
          },
          ...expenseRoutes,
          ...commissionRoutes,
        ],
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'configurations',
        path: 'configurations',
        component: () => import('../pages/configurations/ConfigurationsPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
        meta: { requiresAuth: true },
      },
      ...importRoutes,
      {
        name: 'users',
        path: '/users',
        component: RouteViewComponent,
        children: [
          {
            name: 'active-users',
            path: 'active-users',
            component: () => import('../pages/users/activeUser/UsersPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'staff',
            path: 'staff',
            component: () => import('../pages/users/staff/StaffPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'invitations',
            path: 'invitations',
            component: () => import('../pages/users/invitations/InvitationsPage.vue'),
            meta: { requiresAuth: true },
          },
        ],
      },
      {
        name: 't-payments',
        path: 't-payments',
        component: () => import('../pages/qrcodes/TicketPaymentPage.vue'),
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    path: '/',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'invited-user-activation',
        path: 'invited-user-activation/:uuid',
        component: () => import('../pages/auth/InvitedUserAccountActivation/InvitedUserActivation.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'reset-password',
        path: 'reset-password',
        component: () => import('../pages/auth/ResetPassword.vue'),
      },
      {
        name: 'reset-password-success',
        path: 'reset-password-success',
        component: () => import('../pages/auth/GotoLoginAfterPasswordChange.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: 'business-options-menu',
    path: '/business-options-menu',
    component: () => import('../pages/Dashboards/BusinessOptionsMenuDashboard.vue'),
  },
  ...businessRoutes,
  {
    name: 'branch-selection',
    path: '/branch-selection',
    component: () => import('../pages/Dashboards/BranchSelectionPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    name: 'location-selection',
    path: '/location-selection',
    component: () => import('../pages/Dashboards/InventoryLocationSelectionPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    name: 'qrcodes',
    path: '/qrcodes/:category',
    component: () => import('../pages/qrcodes/QrCodes.vue'),
    props: true,
  },
  {
    name: 'verifyqr',
    path: '/verifyqr/:uuid',
    component: () => import('../pages/qrcodes/ViewQrCodes.vue'),
    props: true,
  },
]

export default routes
